import {useDisclosure, useDisclosureWithData} from '@sollapay/ui/hooks';
import {forwardRef, useCallback, useImperativeHandle} from 'react';

import {useRemoveMemberMutation} from '../hooks';
import {InviteMemberModal} from './InviteMemberModal';
import {showMemberRemovedToast} from './MemberRemovedToast';
import {RemoveMemberModal} from './RemoveMemberModal';

import type {FirmMemberDTO} from '@sollapay/types';

export interface TeamPageModalsHandle {
  openInvite: () => void;
  openRemove: (member: FirmMemberDTO) => void;
}

export interface TeamPageModalsProps {
  onInviteSuccess: () => void;
}

export const TeamPageModals = forwardRef<TeamPageModalsHandle, TeamPageModalsProps>(
  ({onInviteSuccess}, ref) => {
    const inviteModal = useDisclosure();
    const removeModal = useDisclosureWithData<FirmMemberDTO>();
    const removeMemberMutation = useRemoveMemberMutation();

    const handleInviteSuccess = useCallback(() => {
      inviteModal.close();
      onInviteSuccess();
    }, [inviteModal, onInviteSuccess]);

    const handleConfirmRemove = useCallback(async () => {
      if (!removeModal.data) return;
      try {
        await removeMemberMutation.mutateAsync(removeModal.data.id);
        showMemberRemovedToast(removeModal.data.name ?? removeModal.data.email);
        removeModal.close();
      } catch {
        // Keep the modal open so the user can retry/cancel — error shown via mutation.error
      }
    }, [removeModal, removeMemberMutation]);

    const handleCloseRemove = useCallback(() => {
      removeMemberMutation.reset();
      removeModal.close();
    }, [removeModal, removeMemberMutation]);

    useImperativeHandle(ref, () => ({openInvite: inviteModal.open, openRemove: removeModal.open}), [
      inviteModal,
      removeModal
    ]);

    return (
      <>
        <InviteMemberModal
          isOpen={inviteModal.isOpen}
          onClose={inviteModal.close}
          onSuccess={handleInviteSuccess}
        />
        <RemoveMemberModal
          member={removeModal.data}
          onConfirm={() => void handleConfirmRemove()}
          onClose={handleCloseRemove}
          isLoading={removeMemberMutation.isPending}
          error={removeMemberMutation.error}
        />
      </>
    );
  }
);
TeamPageModals.displayName = 'TeamPageModals';
